/**
 * Per-resource SSE notification stream.
 *
 * Single connection per client. Components subscribe to resource stream keys;
 * the singleton manager handles connection lifecycle, Cloudflare timeout
 * reconnection, and Last-Event-ID replay via Redis Streams.
 *
 * Usage:
 *   useNotificationStream("notify:sdr:123:status", "sdr_status", (data) => {
 *     queryClient.setQueryData(["processing-status", ...], data);
 *   });
 */

import { useEffect, useRef, useCallback } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import type { QueryClient, QueryKey } from "react-query";
import { authorizedUser } from "src/helpers/Auth";
import { TAB_ID } from "src/helpers/tabId";
import { nuclearn_api_url } from "src/App";

type EventCallback = (data: Record<string, unknown>) => void;
type HealthListener = (healthy: boolean) => void;

interface Subscription {
  streamKey: string;
  eventType: string;
  callback: EventCallback;
}

// Reconnect grace period (ms) after server sends reconnect event
const RECONNECT_GRACE_MS = 1000;
// Max backoff for error retries
const MAX_RETRY_DELAY_MS = 30000;
// Debounce subscription changes to batch rapid subscribe/unsubscribe.
// 500ms absorbs page-transition churn (multiple useEffect cleanups +
// re-subscribes within a render burst) into a single reconnect.
const RECONNECT_DEBOUNCE_MS = 500;

// Delay before announcing "unhealthy" to listeners. Absorbs planned
// reconnects (server-initiated 85s timeout, subscription churn) so
// consumers don't get spurious invalidations every time the connection
// cycles. If SSE recovers within this window, no listener fires.
const HEALTH_DOWN_GRACE_MS = 5000;

/**
 * Thrown from onopen when the server responds with a 4xx. fetchEventSource
 * treats a throw from onerror as a fatal signal and stops retrying, which
 * is what we want for auth failures (401/403) and malformed requests (400).
 * Retrying a 401 indefinitely burns CPU and log lines in a stale tab.
 */
class FatalSSEError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "FatalSSEError";
  }
}

class NotificationStreamManager {
  private subscriptions = new Map<string, Set<Subscription>>();
  private abortController: AbortController | null = null;
  private lastEventId: string | null = null;
  private retryCount = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _connectionHealthy = false;
  private _announcedHealthy = false;
  private healthListeners = new Set<HealthListener>();
  private healthDownTimer: ReturnType<typeof setTimeout> | null = null;

  get connectionHealthy(): boolean {
    return this._connectionHealthy;
  }

  // React Query's refetchInterval callback only re-evaluates on query state
  // transitions. When SSE drops mid-session with no data events in flight,
  // consumers relying on isNotificationStreamHealthy() as the sole polling
  // gate can get wedged. Consumers register here to react to health flips.
  /**
   * Forget the last event id, so the next connection subscribes fresh
   * (backfill, not a live resume). For a change of signed-in user: resuming
   * from where the previous session left off would deliver what happened
   * while signed out as live events — toasts for history.
   */
  resetCursor(): void {
    this.lastEventId = null;
  }

  /** The composite id of the last event received, or null before any. */
  get cursor(): string | null {
    return this.lastEventId;
  }

  subscribeHealth(listener: HealthListener): () => void {
    this.healthListeners.add(listener);
    return () => {
      this.healthListeners.delete(listener);
    };
  }

  private notifyHealthListeners(healthy: boolean): void {
    for (const listener of this.healthListeners) {
      try {
        listener(healthy);
      } catch (err) {
        console.error("SSE health listener error:", err);
      }
    }
  }

  private setHealth(healthy: boolean): void {
    if (this._connectionHealthy === healthy) return;
    this._connectionHealthy = healthy;

    if (healthy) {
      // Recovered. Cancel any pending "unhealthy" announcement, and if we
      // had already announced unhealthy (grace expired), announce healthy.
      if (this.healthDownTimer) {
        clearTimeout(this.healthDownTimer);
        this.healthDownTimer = null;
      }
      if (!this._announcedHealthy) {
        this._announcedHealthy = true;
        this.notifyHealthListeners(true);
      }
      return;
    }

    // Transitioning to unhealthy. Only announce if we had previously
    // announced healthy AND we stay unhealthy past the grace period.
    if (!this._announcedHealthy || this.healthDownTimer) return;
    this.healthDownTimer = setTimeout(() => {
      this.healthDownTimer = null;
      if (!this._connectionHealthy && this._announcedHealthy) {
        this._announcedHealthy = false;
        this.notifyHealthListeners(false);
      }
    }, HEALTH_DOWN_GRACE_MS);
  }

  subscribe(sub: Subscription): () => void {
    const key = sub.streamKey;
    if (!this.subscriptions.has(key)) {
      this.subscriptions.set(key, new Set());
    }
    this.subscriptions.get(key)!.add(sub);

    this.scheduleReconnect();

    return () => {
      const subs = this.subscriptions.get(key);
      if (subs) {
        subs.delete(sub);
        if (subs.size === 0) {
          this.subscriptions.delete(key);
        }
      }
      if (this.subscriptions.size === 0) {
        this.disconnect();
      } else {
        this.scheduleReconnect();
      }
    };
  }

  private getStreamKeys(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.disconnect();
      this.connect();
    }, RECONNECT_DEBOUNCE_MS);
  }

  private disconnect(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.setHealth(false);
  }

  private async connect(): Promise<void> {
    const keys = this.getStreamKeys();
    if (keys.length === 0) return;
    if (this.abortController) return; // Already connecting

    this.abortController = new AbortController();

    const user = authorizedUser();
    if (!user.Authorization) {
      // Must clear abortController before returning — the `if (this.abortController) return`
      // guard at the top of connect() would otherwise wedge the manager: no future
      // subscribe/reconnect would ever retry, even once the user re-authenticates.
      this.abortController = null;
      this.setHealth(false);
      return;
    }

    const baseUrl = nuclearn_api_url?.startsWith("/")
      ? `${window.location.origin}${nuclearn_api_url}`
      : nuclearn_api_url;

    const url = new URL(`${baseUrl}/notifications/stream`);
    keys.forEach((k) => url.searchParams.append("sub", k));
    url.searchParams.set("tab", TAB_ID);

    try {
      await fetchEventSource(url.toString(), {
        method: "GET",
        headers: {
          Authorization: user.Authorization,
          ...(this.lastEventId ? { "Last-Event-ID": this.lastEventId } : {}),
        },
        signal: this.abortController.signal,
        openWhenHidden: true,

        onopen: async (response) => {
          if (response.ok) {
            this.retryCount = 0;
            this.setHealth(true);
            return;
          }
          // 4xx = don't retry: auth failure, bad request, etc. Throwing
          // a FatalSSEError from onopen lands in onerror, which re-throws
          // it; fetchEventSource catches a throw from onerror and stops
          // the retry loop (see @microsoft/fetch-event-source fetch.js).
          // 5xx, network errors, and 429 remain retryable. 429 is the
          // per-user SSE conn cap (sse:conn:{user_id}) which auto-expires
          // within ~115s via Redis TTL; treating it as fatal would kill
          // the stream until the user reloaded the tab.
          if (
            response.status >= 400 &&
            response.status < 500 &&
            response.status !== 429
          ) {
            throw new FatalSSEError(
              `SSE HTTP ${response.status}`,
              response.status,
            );
          }
          throw new Error(`SSE HTTP ${response.status}`);
        },

        onmessage: (event) => {
          if (event.id) {
            this.lastEventId = event.id;
          }

          if (event.event === "reconnect") {
            // Server forcing reconnect (Cloudflare timeout approaching).
            // Use server-provided jitter to spread reconnect bursts.
            try {
              const reconnectData = JSON.parse(event.data);
              const jitterMs = reconnectData.reconnect_ms ?? 0;
              this.disconnect();
              setTimeout(() => this.connect(), RECONNECT_GRACE_MS + jitterMs);
            } catch {
              this.disconnect();
              setTimeout(() => this.connect(), RECONNECT_GRACE_MS);
            }
            return;
          }

          if (event.event === "connected" || event.event === "error") {
            return;
          }

          // Skip empty events (e.g., pings, keepalives)
          if (!event.data || event.data.trim() === "") {
            return;
          }

          // Parse and dispatch to subscribers by stream key
          try {
            const data = JSON.parse(event.data);
            const streamKey = data._stream_key as string | undefined;
            this.dispatch(event.event, data, streamKey);
          } catch {
            console.warn("Failed to parse SSE event data:", event.data);
          }
        },

        onerror: (err) => {
          this.setHealth(false);
          // Propagate fatal errors (4xx from onopen) to stop the retry
          // loop. Without this, a 401 from an expired token would spin
          // forever at the capped backoff.
          if (err instanceof FatalSSEError) {
            throw err;
          }
          this.retryCount++;
          const base = Math.min(
            1000 * Math.pow(2, this.retryCount),
            MAX_RETRY_DELAY_MS,
          );
          // Jitter prevents 5k clients from all retrying in lockstep when
          // the server restarts and drops every SSE connection at once.
          return base + Math.random() * base * 0.3;
        },

        onclose: () => {
          this.setHealth(false);
          // Auto-reconnect if we still have subscriptions and weren't aborted
          if (
            this.subscriptions.size > 0 &&
            this.abortController &&
            !this.abortController.signal.aborted
          ) {
            this.abortController = null;
            setTimeout(() => this.connect(), RECONNECT_GRACE_MS);
          }
        },
      });
    } catch {
      // Connection failed (aborted, network error, or fatal 4xx). Clear
      // the controller so the next subscription change can trigger a
      // fresh connect — otherwise the `if (this.abortController) return`
      // guard in connect() would wedge us indefinitely.
      this.abortController = null;
      this.setHealth(false);
    }
  }

  private dispatch(
    eventType: string,
    data: Record<string, unknown>,
    streamKey?: string,
  ): void {
    // Route by stream key when available (prevents cross-talk between
    // components subscribing to different resources with the same event type).
    // Falls back to broadcasting to all subscribers for backward compat.
    const buckets = streamKey
      ? [this.subscriptions.get(streamKey)].filter(Boolean)
      : Array.from(this.subscriptions.values());

    for (const subs of buckets) {
      for (const sub of subs!) {
        if (sub.eventType === eventType || sub.eventType === "*") {
          try {
            sub.callback(data);
          } catch (err) {
            console.error("SSE subscriber callback error:", err);
          }
        }
      }
    }
  }
}

// Singleton — shared across all hook instances in the app
const manager = new NotificationStreamManager();

/** Drop the shared stream's resume point; see `resetCursor`. */
export function resetNotificationStreamCursor(): void {
  manager.resetCursor();
}

/** The shared stream's current position (composite Last-Event-ID). */
export function getNotificationStreamCursor(): string | null {
  return manager.cursor;
}

/**
 * Subscribe to a resource notification stream via SSE.
 *
 * @param streamKey - Redis Stream key (e.g., "notify:sdr:123:status"), or null to skip
 * @param eventType - SSE event type to listen for (e.g., "sdr_status"), or "*" for all
 * @param callback - Called with parsed event data when a matching event arrives
 */
export function useNotificationStream(
  streamKey: string | null,
  eventType: string,
  callback: EventCallback,
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const stableCallback = useCallback((data: Record<string, unknown>) => {
    callbackRef.current(data);
  }, []);

  useEffect(() => {
    if (!streamKey) return;

    const sub: Subscription = {
      streamKey,
      eventType,
      callback: stableCallback,
    };

    return manager.subscribe(sub);
  }, [streamKey, eventType, stableCallback]);
}

/**
 * Subscribe to the same event on N stream keys at once. For list views where
 * the set of interesting resources is dynamic (e.g. every active batch in a
 * dataset) and can't be expressed as a fixed number of useNotificationStream
 * calls without violating rules of hooks.
 */
export function useNotificationStreams(
  streamKeys: string[],
  eventType: string,
  callback: EventCallback,
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const stableCallback = useCallback((data: Record<string, unknown>) => {
    callbackRef.current(data);
  }, []);

  // Sorted join so re-renders with the same set in different order don't
  // thrash subscriptions.
  const keysSig = [...streamKeys].sort().join("|");

  useEffect(() => {
    if (streamKeys.length === 0) return;
    const unsubs = streamKeys.map((streamKey) =>
      manager.subscribe({ streamKey, eventType, callback: stableCallback }),
    );
    return () => {
      for (const unsub of unsubs) unsub();
    };
    // keysSig captures identity; streamKeys itself is reconstructed each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysSig, eventType, stableCallback]);
}

/**
 * Returns whether the SSE notification stream is currently healthy.
 * Useful for fallback-to-polling decisions.
 */
export function isNotificationStreamHealthy(): boolean {
  return manager.connectionHealthy;
}

/**
 * Be told when the stream's health flips (after the grace period that
 * absorbs planned reconnects). Returns the unsubscribe function.
 */
export function subscribeNotificationStreamHealth(
  listener: (healthy: boolean) => void,
): () => void {
  return manager.subscribeHealth(listener);
}

/**
 * Force React Query to re-evaluate a query's refetchInterval when SSE health
 * flips to unhealthy. Without this, a query whose refetchInterval callback
 * returned `false` during a healthy period never resumes polling when SSE
 * drops, because nothing triggers the callback to re-run.
 *
 * Pair with any hook that uses isNotificationStreamHealthy() in refetchInterval.
 */
export function useSSEFallbackInvalidation(
  queryClient: QueryClient,
  queryKey: QueryKey,
  enabled: boolean = true,
): void {
  const keySig = JSON.stringify(queryKey);
  useEffect(() => {
    if (!enabled) return;
    return manager.subscribeHealth((healthy) => {
      if (!healthy) queryClient.invalidateQueries(queryKey);
    });
    // keySig captures queryKey identity; queryKey itself is passed through
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, keySig, enabled]);
}
